import { PrimaryButton } from "@nib-components/button";
import Copy from "@nib-components/copy";
import Heading from "@nib-components/heading";
import { TickInCircleSystemIcon } from "@nib/icons";
import InfoBox from "@nib/info-box";
import { Box, Column, Columns, Stack } from "@nib/layout";
import { Link as GatsbyLink } from "gatsby";
import PropTypes from "prop-types";
import React, { useRef } from "react";
import styled from "styled-components";
import breakpoint from "styled-components-breakpoint";
import { Margin, Padding } from "styled-components-spacing";
import formatDollars from "../../../utils/formatDollars";
import OfferTag from "../../OfferTag";
import SalePrice from "../../SalePrice";

import Container from "@nib-components/container";
import useWindowDimensions from "../../../utils/breakpoints";
import withQueryParams from "../../../utils/withQueryParams";
import pregnancyImageMobile from "./images/Mother-with-her-newborn-baby-son-sitting-on-bed-400w.jpg";
import pregnancyImage from "./images/Mother-with-her-newborn-baby-son-sitting-on-bed.jpg";

const StyledInfoBox = styled(InfoBox)`
  padding: 0;
  width: 350px;
  font-weight: 700;

  ${breakpoint("xs")`
    margin: 1rem 0;
    margin-top: 2rem;
  `}
`;

const StyledHeading = styled(Heading)`
  text-align: left;
  ${breakpoint("md")`
    text-align: left;
  `}
`;

const StyledImage = styled.img`
  max-height: 450px;
  width: 100%;

  ${breakpoint("md")`
    max-height: 600px;

  `}
`;

const ProductHighlights = ({ content, product, salesContext }) => {
  const { cta, items, title: subTitle, description, image } = content;
  const scrollRef = useRef();
  const { isMobile } = useWindowDimensions();
  return (
    <Margin vertical={{ xs: 6, md: 9 }}>
      <Container>
        <Padding
          horizontal={{
            xs: 4,
            md: 6,
            lg: 8,
          }}
        >
          <Columns space={{ xs: 2, lg: 4 }} collapseBelow="lg">
            <Column width={{ xs: 1, lg: "1/2" }} style={{ alignSelf: "center" }}>
              <Box>
                {salesContext.isOnSale && salesContext.tagText && (
                  <OfferTag
                    offerTitle={salesContext.tagText}
                    style={{ top: "40px", left: isMobile ? "65%" : "20px" }}
                  />
                )}
                {isMobile ? (
                  <StyledImage
                    src={pregnancyImageMobile} //TODO:: Replace with image.url
                    alt={image?.alt} //TODO:: These should be sourced from a CDN, so that it isn't hardcoded per product.
                  />
                ) : (
                  <StyledImage src={pregnancyImage} alt={image?.alt} />
                )}
              </Box>
            </Column>
            <Column width={{ xs: 1, lg: "1/2" }}>
              <Margin left={{ xs: 0, lg: 8 }}>
                <Stack space={{ xs: 1, md: 4 }}>
                  <Margin top={{ xs: 5 }}>
                    <div ref={scrollRef}>
                      <StyledHeading component="h3" size={{ xs: 3, md: 2 }}>
                        {subTitle}
                      </StyledHeading>
                    </div>
                  </Margin>
                  {description.length &&
                    description.map((text, index) => {
                      return (
                        <Margin top={4} key={index} bottom={5}>
                          <Copy key={index}>{text}</Copy>
                        </Margin>
                      );
                    })}
                  {items.length &&
                    items.map((item, index) => (
                      <Margin bottom={5} key={index}>
                        <StyledInfoBox
                          withBorder={false}
                          withBoxShadow={false}
                          icon={TickInCircleSystemIcon}
                        >
                          {item}
                        </StyledInfoBox>
                      </Margin>
                    ))}
                  {salesContext.isOnSale ? (
                    <SalePrice
                      saleCTA={salesContext.saleCTA}
                      rawAmount={salesContext.salesAmount.raw}
                      salesAmount={salesContext.salesAmount.formatted}
                      nonSaleAmount={salesContext.defaultAmount.formatted}
                      textColor={salesContext.colorHex}
                    />
                  ) : (
                    <Heading size={2} as="h3">
                      {formatDollars(product?.pricing?.amount)}
                    </Heading>
                  )}
                  <Margin top={5} />
                  <PrimaryButton
                    data-tealium-id="p2p_pregnancy-program_product-page_footer-click"
                    size="medium"
                    align="center"
                    as={GatsbyLink}
                    to={withQueryParams(`/checkout/`)}
                    state={{ product }}
                    fullWidth={isMobile}
                  >
                    <Padding horizontal={4}>{salesContext.content.button}</Padding>
                  </PrimaryButton>
                </Stack>
              </Margin>
            </Column>
          </Columns>
        </Padding>
      </Container>
    </Margin>
  );
};

ProductHighlights.propTypes = {
  content: PropTypes.shape({
    contentType: PropTypes.string,
    cta: PropTypes.arrayOf(
      PropTypes.shape({
        copy: PropTypes.string,
        title: PropTypes.string,
      })
    ),
    items: PropTypes.arrayOf(PropTypes.string),
    title: PropTypes.string,
    description: PropTypes.arrayOf(PropTypes.string),
  }),
  image: PropTypes.shape({
    url: PropTypes.string,
    alt: PropTypes.string,
  }),
  product: PropTypes.shape({}),
  salesContext: PropTypes.shape({
    isOnSale: PropTypes.bool,
    tagText: PropTypes.string,
    saleCTA: PropTypes.string,
    salesAmount: PropTypes.shape({
      formatted: PropTypes.string,
    }),
    defaultAmount: PropTypes.shape({
      formatted: PropTypes.string,
    }),
  }),
};

export default ProductHighlights;
